import { useCallback } from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { graphql, useMutation } from 'react-relay';
import { fetchQuery, useRelayEnvironment } from 'react-relay';

import { useFlags } from '@atlaskit/flag/use-flags';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';

import { ROVO_AGENTS_FE_TEAM_NAME } from '../../../common/constants/private.ts';
import {
	getAtlassianStudioAgentDuplicateUrl,
	getStudioPath,
} from '../../../common/utils/atlassian-studio-url/index.tsx';

import type { duplicate_duplicateAgentMutation } from './__generated__/duplicate_duplicateAgentMutation.graphql.ts';
import type { duplicate_hasVersionCapabilityQuery } from './__generated__/duplicate_hasVersionCapabilityQuery.graphql.ts';
import messages from './messages.ts';

/**
 * Builds the absolute redirect URL for a newly duplicated agent in Agent Studio.
 */
const getDuplicatedAgentEditUrl = (siteId: string, agentAri: string, email?: string): string =>
	getStudioPath(`/s/${siteId}/agents/${encodeURIComponent(agentAri)}/v/draft/details`, email);

/**
 * Hook that provides the duplicate agent action with versioning support.
 *
 * Uses Relay `fetchQuery` for version capability check and `useMutation` for the
 * duplicate mutation. When versioning is not enabled or migration hasn't completed,
 * falls back to the legacy FE duplicate flow.
 *
 * Reliability tracking: fires `operational ui taskStart` / `taskSuccess` /
 * `taskFail` GAS events via `useAIMateExperienceTracker` under the
 * `aiMateDuplicateAgent` experience name. Sentry reporting and SLO accounting
 * are handled centrally by the tracker.
 */
export function useDuplicateAgent({
	siteId,
	source,
	email,
}: {
	siteId: string;
	source?: string;
	email?: string;
}): {
	duplicateAgent: (agentId: string, agentAri: string) => Promise<void>;
	isMutating: boolean;
} {
	di(fetchQuery, fg);
	const environment = useRelayEnvironment();
	const { showFlag } = useFlags();
	const { formatMessage } = useIntl();
	const experienceTracker = useAIMateExperienceTracker();

	// eslint-disable-next-line @atlassian/relay/no-wrapping-hooks
	const [commitMutation, isMutating] = useMutation<duplicate_duplicateAgentMutation>(graphql`
		mutation duplicate_duplicateAgentMutation($agentId: ID!) {
			agentStudio_duplicateAgent(id: $agentId) @optIn(to: "AgentStudio") {
				agent {
					id
				}
				success
				errors {
					message
				}
			}
		}
	`);

	const duplicateAgent = useCallback(
		async (agentId: string, agentAri: string): Promise<void> => {
			const legacyDuplicateUrl = getAtlassianStudioAgentDuplicateUrl(
				siteId,
				agentId,
				source,
				email,
			);

			// When versioning FG is off, use legacy duplicate flow
			if (!fg('rovo_agent_versioning_enabled')) {
				window.open(legacyDuplicateUrl);
				return;
			}

			experienceTracker.start({
				id: ExperienceName.DUPLICATE_AGENT,
				name: ExperienceName.DUPLICATE_AGENT,
			});

			// Check if versioning migration has completed for this site
			// hasVersionCapability will only be true after BE backfill is done
			try {
				const data = await fetchQuery<duplicate_hasVersionCapabilityQuery>(
					environment,
					graphql`
						query duplicate_hasVersionCapabilityQuery($cloudId: String!) {
							agentStudio_hasVersionCapability(cloudId: $cloudId) @optIn(to: "AgentStudio") {
								__typename
								... on AgentStudioHasVersionCapability {
									hasVersionCapability
								}
							}
						}
					`,
					{ cloudId: siteId },
					{ fetchPolicy: 'store-or-network' },
				).toPromise();

				const result = data?.agentStudio_hasVersionCapability;
				const hasVersionCapability =
					result?.__typename === 'AgentStudioHasVersionCapability'
						? Boolean(result.hasVersionCapability)
						: false;

				if (!hasVersionCapability) {
					experienceTracker.succeed({ name: ExperienceName.DUPLICATE_AGENT });
					// Opening in same tab because, we cannot open in new tab after async operation, because browsers prevent `.open` after certain duration
					window.location.assign(legacyDuplicateUrl);
					return;
				}
			} catch (error) {
				experienceTracker.fail({
					name: ExperienceName.DUPLICATE_AGENT,
					error: error instanceof Error ? error : new Error(String(error)),
					teamName: ROVO_AGENTS_FE_TEAM_NAME,
				});
				// If version capability check fails, fall back to legacy flow
				// Opening in same tab because, we cannot open in new tab after async operation, because browsers prevent `.open` after certain duration
				window.location.assign(legacyDuplicateUrl);
				return;
			}

			// Call BE mutation to duplicate the agent
			await new Promise<void>((resolve, reject) => {
				commitMutation({
					variables: { agentId: agentAri },
					onCompleted: (response) => {
						const mutationResult = response.agentStudio_duplicateAgent;
						if (mutationResult?.success && mutationResult?.agent?.id) {
							experienceTracker.succeed({ name: ExperienceName.DUPLICATE_AGENT });
							// Opening in same tab because, we cannot open in new tab after async operation, because browsers prevent `.open` after certain duration
							window.location.assign(
								getDuplicatedAgentEditUrl(siteId, mutationResult.agent.id, email),
							);
							resolve();
						} else {
							const message =
								mutationResult?.errors?.[0]?.message ??
								formatMessage(messages.duplicateAgentErrorDescription);
							const error = new Error(message);
							experienceTracker.fail({
								name: ExperienceName.DUPLICATE_AGENT,
								error,
								teamName: ROVO_AGENTS_FE_TEAM_NAME,
							});
							showFlag({
								title: formatMessage(messages.duplicateAgentErrorTitle),
								description: message,
								appearance: 'error',
								isAutoDismiss: true,
							});
							reject(error);
						}
					},
					onError: (error) => {
						experienceTracker.fail({
							name: ExperienceName.DUPLICATE_AGENT,
							error,
							teamName: ROVO_AGENTS_FE_TEAM_NAME,
						});
						showFlag({
							title: formatMessage(messages.duplicateAgentErrorTitle),
							description: error.message ?? formatMessage(messages.duplicateAgentErrorDescription),
							appearance: 'error',
							isAutoDismiss: true,
						});
						reject(error);
					},
				});
			});
		},
		[
			commitMutation,
			email,
			environment,
			experienceTracker,
			formatMessage,
			showFlag,
			siteId,
			source,
		],
	);

	return {
		duplicateAgent,
		isMutating,
	};
}
